/**
 * The account home composition: the three own-account reads, run through
 * `buildAccountHomeView`, mounted in the frame's top-to-bottom order, with
 * the two-column grid at `lg`. The only file on this screen that calls an
 * SDK hook.
 */
import { useAwardGetMine } from "@sportsmet/contest-sdk/private";
import { usePoolListMine } from "@sportsmet/pool-sdk/private";
import {
  useFanFollowRequestCount,
  useProfileGet,
} from "@sportsmet/profile-sdk/private";
import type { ReactNode } from "react";

import type { typeAwardChip } from "../awards/awardChips";
import { MedalsBlock } from "../awards/MedalsBlock";
import { AccountFavoriteTeamsRow } from "./AccountFavoriteTeamsRow";
import {
  AWARDS_UNAVAILABLE,
  MEDALS_SECTION_TITLE,
  SCOPE_ALL_TIME_LABEL,
  SCOPE_TABS_LABEL,
  SCOPE_THIS_SEASON_LABEL,
} from "./accountHomeCopy";
import { buildAccountHomeView } from "./accountHomeState";
import { AccountPoolsList } from "./AccountPoolsList";
import { AccountProfileHeader } from "./AccountProfileHeader";
import { InviteFriendsCard } from "../invites/InviteFriendsCard";
import { AccountSettingsGroup } from "./AccountSettingsGroup";

export type typeAccountHomePaneProps = {
  onChipSelect: (input: {
    chip: typeAwardChip;
    poolName: string | null;
  }) => void;
  onSignOut: () => void;
  isSigningOut: boolean;
};

export const AccountHomePane = (props: typeAccountHomePaneProps): ReactNode => {
  const profileQuery = useProfileGet({});
  // ISSUE-800. This read is kept fresh here, not by a mutation dependency.
  // A custom award is given by a commissioner, never by its recipient.
  // That give runs in the commissioner's browser, on their own query cache.
  // No dependency declared on the mutation can reach this fan's cache.
  // So the read shortens its own freshness instead, the way
  // `poolAwards/PoolAwardsCard.tsx` already does for `awardCustomList`.
  // Contrast `fanProfile/FanProfilePane.tsx`, which the giver's own session
  // does reach, so that read keeps the app default.
  const awardsQuery = useAwardGetMine(
    {},
    { staleTime: 0, refetchOnMount: "always" },
  );
  const poolsQuery = usePoolListMine({});
  // ISSUE-1372: the Followers row's pill. A failed read shows no pill rather
  // than an error; the Followers screen itself reports its own failures.
  const followRequestCountQuery = useFanFollowRequestCount({});

  const view = buildAccountHomeView({
    isProfileLoading: profileQuery.isPending,
    hasProfileFailed: profileQuery.isError || Boolean(profileQuery.data?.error),
    profile: profileQuery.data?.data?.profile,
    areAwardsLoading: awardsQuery.isPending,
    haveAwardsFailed: awardsQuery.isError || Boolean(awardsQuery.data?.error),
    awards: awardsQuery.data?.data,
    arePoolsLoading: poolsQuery.isPending,
    havePoolsFailed: poolsQuery.isError || Boolean(poolsQuery.data?.error),
    poolItems: poolsQuery.data?.data?.items,
  });

  return (
    <div className="flex flex-col gap-6 pb-24" data-testid="account-home">
      <AccountProfileHeader
        displayName={view.displayName}
        username={view.username}
        photoUrl={view.photoUrl}
        testId="account-header"
      />

      <div
        className="grid grid-cols-1 gap-6 lg:grid-cols-2"
        data-testid="account-grid"
      >
        <div className="flex flex-col gap-6">
          <MedalsBlock
            isLoading={view.areAwardsLoading}
            hasFailed={view.haveAwardsFailed}
            thisSeason={view.thisSeason}
            allTime={view.allTime}
            chips={view.chips}
            title={MEDALS_SECTION_TITLE}
            thisSeasonLabel={SCOPE_THIS_SEASON_LABEL}
            allTimeLabel={SCOPE_ALL_TIME_LABEL}
            tabsLabel={SCOPE_TABS_LABEL}
            unavailableMessage={AWARDS_UNAVAILABLE}
            onChipSelect={(chip) => {
              props.onChipSelect({
                chip,
                poolName: view.poolNameById[chip.poolId] ?? null,
              });
            }}
            testId="account-medals"
          />
          <AccountFavoriteTeamsRow
            teamIds={view.favoriteTeamIds}
            testId="account-teams-section"
          />
        </div>

        <div className="flex flex-col gap-6">
          <AccountPoolsList
            isLoading={view.arePoolsLoading}
            hasFailed={view.havePoolsFailed}
            cards={view.poolCards}
            testId="account-pools-section"
          />
          {/* ISSUE-1404: v1's Settings > Invite friends. */}
          <InviteFriendsCard testId="account-invite-friends" />
          <AccountSettingsGroup
            onSignOut={props.onSignOut}
            isSigningOut={props.isSigningOut}
            followRequestCount={
              followRequestCountQuery.data?.data?.requestCount ?? 0
            }
            testId="account-settings-section"
          />
        </div>
      </div>
    </div>
  );
};
